import { type FC, type ReactElement, useLayoutEffect } from "react";
import { DashboardNotificationSectionFrame } from "web_myplann/Dashboard/Components/DashboardNotificationSectionFrame";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import type { NotificationCenterTab } from "web_myplann/NotificationCenter/Constants/notificationCenterTabs";

type TimelineItemComponent<T> = FC<{
	item: T;
	tab: NotificationCenterTab;
	onClick?: (item: T) => void;
	isLast?: boolean;
}>;

interface DashboardTimelinePreviewProps<T> {
	title: string;
	items: T[];
	hasMore: boolean;
	isLoading: boolean;
	showAllTab: NotificationCenterTab;
	ItemComponent: TimelineItemComponent<T>;
	testId: string;
	onItemClick?: (item: T) => void;
	onEmptyChange?: (isEmpty: boolean) => void;
}

export const DashboardTimelinePreview = <T extends { id: string }>({
	title,
	items,
	hasMore,
	isLoading,
	showAllTab,
	ItemComponent,
	testId,
	onItemClick,
	onEmptyChange,
}: DashboardTimelinePreviewProps<T>): ReactElement | null => {
	const isEmpty = !isLoading && items.length === 0;

	useLayoutEffect(() => {
		onEmptyChange?.(isEmpty);
	}, [onEmptyChange, isEmpty]);

	if (isLoading || isEmpty) {
		return null;
	}

	const showAllHref = hasMore
		? Routing.Notifications.path(showAllTab)
		: undefined;

	return (
		<DashboardNotificationSectionFrame
			title={title}
			showAllHref={showAllHref}
			testId={testId}
		>
			{items.map((item, index) => (
				<ItemComponent
					key={item.id}
					item={item}
					tab={showAllTab}
					onClick={onItemClick}
					isLast={index === items.length - 1}
				/>
			))}
		</DashboardNotificationSectionFrame>
	);
};
